import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Crown, Check, Zap, Users, TrendingUp,
  Shield, Target, Star, BarChart3, Calendar,
  Sparkles, AlertCircle, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Razorpay SDK Loader ────────────────────────────────────────────────────
// Load the official Razorpay checkout.js script on demand.
// The script is only injected once, no matter how many times this is called.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
// ──────────────────────────────────────────────────────────────────────────

const PLAN_COLORS = {
  monthly:   { ring: 'ring-primary',   badge: 'bg-primary',   btn: 'bg-primary hover:bg-primary/90' },
  quarterly: { ring: 'ring-purple-500', badge: 'bg-purple-500', btn: 'bg-purple-500 hover:bg-purple-600' },
  yearly:    { ring: 'ring-amber-500',  badge: 'bg-gradient-to-r from-amber-500 to-orange-500', btn: 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' },
};

const Subscription = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null); // which plan is being processed
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    loadPlans();
    if (isAuthenticated) loadSubscriptionStatus();
  }, [isAuthenticated]);

  const loadPlans = async () => {
    try {
      const response = await api.get('/subscription/plans');
      setPlans(response.data);
    } catch {
      // Silently fall back to hardcoded plans
      setPlans({
        free_features: ['Unlimited practice questions', 'Basic test creation', 'Progress tracking', 'Community Discord access'],
        premium_features: ['Advanced analytics & insights', 'Challenge & practice with friends', 'Mistake tracker with spaced repetition', 'Exclusive Discord roles & channels'],
        plans: {
          monthly:   { price: 49,  duration_days: 30,  name: 'Monthly Plan' },
          quarterly: { price: 99,  duration_days: 90,  name: 'Quarterly Plan' },
          yearly:    { price: 299, duration_days: 365, name: 'Yearly Plan' },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionStatus = async () => {
    try {
      const response = await api.get('/subscription/status');
      setCurrentSubscription(response.data);
    } catch {
      // Non-critical — ignore
    }
  };

  // ── Razorpay Checkout Flow ──────────────────────────────────────────────
  const handleSubscribe = useCallback(async (planKey) => {
    if (!isAuthenticated) {
      toast.error('Please sign in to subscribe');
      navigate('/auth?mode=login');
      return;
    }

    if (processingPlan) return; // prevent double-clicks
    setProcessingPlan(planKey);

    try {
      // 1. Load Razorpay SDK
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        toast.error('Could not load payment gateway. Check your internet connection.');
        return;
      }

      // 2. Create order on backend
      let order;
      try {
        const res = await api.post(`/subscription/create-order?plan=${planKey}`);
        order = res.data;
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to create order. Please try again.';
        toast.error(msg);
        return;
      }

      // 3. Open Razorpay checkout modal
      const planDetails = plans?.plans?.[planKey];
      const keyId = order.key_id;

      if (order.dev_mode) {
        toast.error('Payment gateway is in development fallback mode. Please configure Razorpay keys on server.');
        return;
      }

      if (!keyId || keyId === 'not_configured') {
        toast.error('Payment gateway is not configured. Contact support.');
        return;
      }

      if (!order.order_id || !String(order.order_id).startsWith('order_')) {
        toast.error('Invalid payment order received. Please try again.');
        return;
      }

      const options = {
        key: keyId,
        amount: order.amount,        // in paise
        currency: order.currency || 'INR',
        name: 'ExamDex',
        description: planDetails?.name || 'Premium Subscription',
        order_id: order.order_id,
        image: '/logo192.png',

        // ── Payment Success Handler ──────────────────────────────────────
        handler: async (response) => {
          try {
            // 4. Verify signature on backend (HMAC-SHA256)
            const verifyRes = await api.post('/subscription/verify-payment', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planKey,
            });

            if (verifyRes.data.success) {
              toast.success(verifyRes.data.message || '🎉 Premium activated!', { duration: 6000 });
              // Refresh user context so isPremium flips everywhere instantly
              if (refreshUser) await refreshUser();
              await loadSubscriptionStatus();
              // Small delay then navigate to dashboard
              setTimeout(() => navigate('/dashboard'), 1500);
            } else {
              toast.error('Payment verification failed. Contact support with your payment ID.');
            }
          } catch (err) {
            const msg = err.response?.data?.detail || 'Payment verification failed. Please contact support.';
            toast.error(msg);
          }
        },

        // ── Payment Failed Handler ───────────────────────────────────────
        modal: {
          ondismiss: () => {
            toast.info('Payment cancelled.');
            setProcessingPlan(null);
          },
        },

        prefill: {
          name:  user?.name  || '',
          email: user?.email || '',
        },

        notes: {
          plan: planKey,
          user_id: String(user?.id || ''),
        },

        theme: {
          color: '#3B82F6', // ExamDex primary blue
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on('payment.failed', (response) => {
        const errCode = response.error?.code;
        const errDesc = response.error?.description || 'Payment failed. Please try again.';
        const errReason = response.error?.reason;
        const errSource = response.error?.source;
        const errStep = response.error?.step;
        const errMetaOrderId = response.error?.metadata?.order_id;

        if (process.env.NODE_ENV !== 'production') {
          // Keep complete payload in dev for exact Razorpay diagnostics.
          console.error('Razorpay payment.failed payload:', response.error || response);
        }

        if (/international cards are not supported/i.test(errDesc) || /international/i.test(String(errReason || ''))) {
          toast.error('This Razorpay account currently accepts domestic cards only. In test mode, use a domestic test card or UPI (success@razorpay).');
          setProcessingPlan(null);
          return;
        }

        if (errCode === 'BAD_REQUEST_ERROR' || errReason === 'bad_request_error') {
          toast.error('Razorpay rejected this payment request. Use a domestic test method (UPI/card), and verify account payment methods in Razorpay dashboard.');
          setProcessingPlan(null);
          return;
        }

        const technicalContext = [errReason, errSource, errStep, errMetaOrderId].filter(Boolean).join(' • ');
        const prefix = errCode || 'PAYMENT_ERROR';
        toast.error(technicalContext ? `Payment failed (${prefix}): ${errDesc} [${technicalContext}]` : `Payment failed (${prefix}): ${errDesc}`);

        setProcessingPlan(null);
      });

      rzp.open();
    } finally {
      // Reset after a small delay (modal may still be open)
      setTimeout(() => setProcessingPlan(null), 500);
    }
  }, [isAuthenticated, processingPlan, plans, user, navigate, refreshUser]);
  // ──────────────────────────────────────────────────────────────────────────

  const isPremium = currentSubscription?.is_premium || user?.premium;
  const expiresAt = currentSubscription?.subscription?.expires_at;
  const daysRemaining = currentSubscription?.subscription?.days_remaining;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Crown size={16} />
            <span>Premium Plans</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
            Upgrade Your Preparation
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get access to advanced analytics, challenge friends, and personalized mistake tracking
          </p>
        </div>

        {/* ── Active Premium Banner ────────────────────────────────────── */}
        {isPremium && (
          <Card className="p-6 mb-8 bg-gradient-to-r from-amber-500 to-amber-600 text-white rounded-2xl shadow-lg">
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <Crown size={36} />
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-xl font-bold">You're a Premium Member! 🎉</h3>
                <p className="text-white/80 mt-1">
                  {expiresAt
                    ? `Expires: ${new Date(expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}${daysRemaining != null ? ` · ${daysRemaining} days remaining` : ''}`
                    : 'All premium features unlocked'}
                </p>
              </div>
              <div className="flex gap-2">
                <Badge className="bg-white/20 text-white border-0 text-sm px-3 py-1">
                  <Sparkles size={12} className="mr-1" />
                  ACTIVE
                </Badge>
              </div>
            </div>
          </Card>
        )}

        {/* ── Pricing Grid ─────────────────────────────────────────────── */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">

          {/* Free Plan */}
          <Card className="p-6 bg-card rounded-2xl border border-border">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-foreground mb-2">Free</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-foreground">₹0</span>
                <span className="text-muted-foreground">/forever</span>
              </div>
            </div>
            <ul className="space-y-3 mb-6">
              {(plans?.free_features || []).map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check size={16} className="text-green-500 flex-shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full rounded-full" disabled>
              Current Plan
            </Button>
          </Card>

          {/* Dynamic Premium Plans */}
          {Object.entries(plans?.plans || {}).map(([key, plan]) => {
            const colors = PLAN_COLORS[key] || PLAN_COLORS.monthly;
            const isPopular = key === 'quarterly';
            const isBestValue = key === 'yearly';
            const isProcessing = processingPlan === key;

            return (
              <Card
                key={key}
                className={`p-6 bg-card rounded-2xl border-2 ${colors.ring} ring-1 relative transition-shadow hover:shadow-xl`}
              >
                {isPopular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white border-0 px-3">
                    POPULAR
                  </Badge>
                )}
                {isBestValue && (
                  <Badge className={`absolute -top-3 left-1/2 -translate-x-1/2 ${colors.badge} text-white border-0 px-3`}>
                    🎉 BEST VALUE
                  </Badge>
                )}

                <div className="mb-6">
                  <h3 className="text-xl font-bold text-foreground mb-1 capitalize">{key}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-foreground">₹{plan.price}</span>
                    <span className="text-muted-foreground">/{key === 'monthly' ? 'mo' : key === 'quarterly' ? '3mo' : 'yr'}</span>
                  </div>
                  {key === 'quarterly' && (
                    <p className="text-xs text-green-600 mt-1 font-medium">Save ₹{49 * 3 - 99} vs monthly</p>
                  )}
                  {key === 'yearly' && (
                    <p className="text-xs text-amber-600 mt-1 font-medium">Save ₹{49 * 12 - 299} vs monthly (₹{Math.round(299/12)}/mo)</p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {(plans?.premium_features || []).map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground">
                      <Check size={16} className="text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSubscribe(key)}
                  disabled={isProcessing || !!processingPlan || isPremium}
                  className={`w-full rounded-full text-white shadow-md ${colors.btn} disabled:opacity-60`}
                >
                  {isPremium ? (
                    'Already Premium'
                  ) : isProcessing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 size={16} className="animate-spin" />
                      Opening Payment...
                    </span>
                  ) : (
                    <>
                      <Crown size={16} className="mr-1.5" />
                      Subscribe Now
                    </>
                  )}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* ── Features Grid ────────────────────────────────────────────── */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">What's Included</h2>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: BarChart3, title: 'Deep Analytics',     desc: 'Subject-wise accuracy & time analysis' },
              { icon: TrendingUp, title: 'Mistake Tracker',   desc: 'Spaced repetition for wrong answers' },
              { icon: Target,    title: 'Friend Challenges',  desc: 'Compete with friends on quizzes' },
              { icon: Shield,    title: 'Bookmarks',          desc: 'Save important questions for review' },
            ].map((feature, i) => (
              <Card key={i} className="p-4 bg-card rounded-xl border border-border text-center hover:shadow-md transition-shadow">
                <feature.icon className="text-primary mx-auto mb-2" size={28} />
                <h4 className="font-semibold text-foreground text-sm">{feature.title}</h4>
                <p className="text-xs text-muted-foreground mt-1">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>

        
        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <Card className="p-6 bg-card rounded-2xl border border-border">
          <h3 className="text-lg font-bold text-foreground mb-4">Frequently Asked Questions</h3>
          <div className="space-y-4">
            
            <div>
              <p className="font-medium text-foreground">What payment methods are accepted?</p>
              <p className="text-sm text-muted-foreground mt-1">All major credit/debit cards, UPI (Google Pay, PhonePe, Paytm), net banking, and wallets through Razorpay.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Can I cancel anytime?</p>
              <p className="text-sm text-muted-foreground mt-1">Yes. You retain premium access until the end of your billing period. No automatic renewal without your action.</p>
            </div>
            <div>
              <p className="font-medium text-foreground">What happens when my subscription expires?</p>
              <p className="text-sm text-muted-foreground mt-1">You'll keep all free features. Premium features like analytics, mistake tracker, and friend challenges are locked until renewal.</p>
            </div>
          </div>
        </Card>

      </div>
    </Layout>
  );
};

export default Subscription;
